import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Badge, Button, Card, Dialog, Switch } from '@/ds';
import { EmptyState } from '@/components/EmptyState';
import { useOperation, useWallets, type WalletSummary } from '@/api/hooks';
import { useIsMobile } from '@/lib/useMediaQuery';
import { WalletFormDialog } from '../wallets/WalletFormDialog';
import { KIND_LABELS, WalletBalance } from '../wallets/walletView';

export function WalletsTab() {
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const wallets = useWallets();
  const toggle = useOperation<{ id: string; isActive: boolean }>('wallet.update');
  const remove = useOperation<{ id: string }>('wallet.delete');

  const [editing, setEditing] = useState<WalletSummary | null>(null);
  const [deleting, setDeleting] = useState<WalletSummary | null>(null);

  const rows = wallets.data?.wallets ?? [];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-5)' }}>
      <p
        style={{
          font: '400 13px/1.5 var(--font-text)',
          color: 'var(--text-muted)',
          maxWidth: 640,
        }}
      >
        Кошельки описаны в коде: добавить кошелёк или сменить его название и источник данных можно
        только доработкой. Здесь меняются заметки и включённость. Отключённый кошелёк исчезает из
        выпадающих списков, история платежей с него сохраняется.
      </p>

      {rows.length === 0 ? (
        <EmptyState
          icon="CreditCard"
          title="Кошельков пока нет"
          description="Кошельки появляются при запуске сервера из реестра в коде."
        />
      ) : (
        <Card padding={isMobile ? 'sm' : 'md'}>
          <div className="kj-scroll-x">
            <table className="kj-table">
              <thead>
                <tr>
                  <th>Название</th>
                  <th>Тип</th>
                  <th>Остаток</th>
                  <th>Курс оплат</th>
                  <th>Активен</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {rows.map((wallet) => (
                  <tr key={wallet.id}>
                    <td data-label="Название" style={{ color: 'var(--text-strong)', fontWeight: 500 }}>
                      {wallet.name}
                      {wallet.isActive ? null : (
                        <span style={{ marginLeft: 8 }}>
                          <Badge tone="neutral">Отключён</Badge>
                        </span>
                      )}
                    </td>
                    <td data-label="Тип" style={{ color: 'var(--text-muted)' }}>
                      {KIND_LABELS[wallet.kind] ?? wallet.kind}
                    </td>
                    <td data-label="Остаток">
                      <WalletBalance wallet={wallet} size="sm" />
                    </td>
                    <td data-label="Курс оплат" style={{ color: 'var(--text-muted)' }}>
                      {wallet.ratePolicy === 'market' ? 'курс дня' : 'курс пополнений'}
                    </td>
                    <td data-label="Активен">
                      <Switch
                        checked={wallet.isActive}
                        onChange={(e) =>
                          toggle.mutate({ id: wallet.id, isActive: e.target.checked })
                        }
                      />
                    </td>
                    <td data-label="" style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => navigate(`/wallets/${wallet.id}`)}
                      >
                        Открыть
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => setEditing(wallet)}>
                        Изменить
                      </Button>
                      {wallet.code === null ? (
                        <Button variant="ghost" size="sm" onClick={() => setDeleting(wallet)}>
                          Удалить
                        </Button>
                      ) : null}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {editing ? (
        <WalletFormDialog wallet={editing} open onClose={() => setEditing(null)} />
      ) : null}

      <Dialog
        open={deleting !== null}
        title="Удалить кошелёк"
        description={
          deleting
            ? `«${deleting.name}» пропадёт из списков. История пополнений и платежей останется, расчёты прошлых месяцев не изменятся.`
            : ''
        }
        onClose={() => setDeleting(null)}
        footer={
          <>
            <Button variant="secondary" onClick={() => setDeleting(null)}>
              Отмена
            </Button>
            <Button
              variant="danger"
              loading={remove.isPending}
              onClick={() => {
                if (!deleting) return;
                remove.mutate({ id: deleting.id }, { onSuccess: () => setDeleting(null) });
              }}
            >
              Удалить
            </Button>
          </>
        }
      />
    </div>
  );
}
